import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@app/environment/environment';
import {
  AcceptExperiencedQualificationRequest,
  AcceptKnowledgeTestRequest,
  Authentication2FaCreatedResponse,
  ForgotPasswordRequest,
  Person,
  SetInvestmentStateRequest,
  SetInvestorQualificationRequest,
  SetKnowledgeTestRequest,
  UserAccount,
  UserConfirmEmailRequest,
  UserGeneralStatementRequest,
  UserPasswordChange,
  UserRegistrationRequest,
  UserResetPasswordRequest,
  Verify2faRequest,
} from '@app/models/account.model';
import { LegalEntityDetails } from '@app/models/project.models';
import { PaginatedData } from '@app/models/shared.models';
import { AuthenticationResponse } from '@app/models/auth.model';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly API_URL = environment.apiUrl + '/Account';
  private http = inject(HttpClient);

  register(payload: UserRegistrationRequest): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/Register`, payload);
  }

  confirmEmail(payload: UserConfirmEmailRequest): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/ConfirmEmail`, payload);
  }

  forgotPassword(payload: ForgotPasswordRequest): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/PasswordForgot`, payload);
  }

  resetPassword(
    payload: UserResetPasswordRequest,
  ): Observable<AuthenticationResponse> {
    return this.http.post<AuthenticationResponse>(
      `${this.API_URL}/PasswordReset`,
      payload,
    );
  }

  changePassword(
    payload: UserPasswordChange,
  ): Observable<AuthenticationResponse> {
    return this.http.post<AuthenticationResponse>(
      `${this.API_URL}/PasswordChange`,
      payload,
    );
  }

  enable2fa(): Observable<Authentication2FaCreatedResponse> {
    return this.http.post<Authentication2FaCreatedResponse>(
      `${this.API_URL}/Enable2fa`,
      {},
    );
  }

  verify2fa(payload: Verify2faRequest): Observable<any> {
    return this.http.post<any>(`${this.API_URL}/Verify2fa`, payload);
  }

  disable2fa(): Observable<any> {
    return this.http.delete<any>(`${this.API_URL}/Disable2fa`);
  }

  getAccountDetails(): Observable<UserAccount> {
    return this.http.get<UserAccount>(`${this.API_URL}`);
  }

  setGeneralTermsAndConditions(
    payload: UserGeneralStatementRequest,
  ): Observable<UserAccount> {
    return this.http.put<UserAccount>(
      `${this.API_URL}/SetGeneralTermsAndConditions`,
      payload,
    );
  }

  getPersonalData(): Observable<Person> {
    return this.http.get<Person>(`${this.API_URL}/PersonalData`);
  }

  setPersonalData(payload: Person): Observable<Person> {
    return this.http.put<Person>(`${this.API_URL}/PersonalData`, payload);
  }

  setInvestorQualification(
    experienced: SetInvestorQualificationRequest,
  ): Observable<UserAccount> {
    return this.http.put<UserAccount>(
      `${this.API_URL}/SetInvestorQualification`,
      experienced,
    );
  }

  setInvestorQualificationForLegalEntity(
    legalEntityId: string,
    experienced: SetInvestorQualificationRequest,
  ): Observable<LegalEntityDetails> {
    return this.http.put<LegalEntityDetails>(
      `${environment.apiUrl}/LegalEntity/${legalEntityId}/SetInvestorQualification`,
      experienced,
    );
  }

  setStartOfKnowledgeTest(): Observable<UserAccount> {
    return this.http.put<UserAccount>(
      `${this.API_URL}/SetStartOfKnowledgeTest`,
      null,
    );
  }

  acceptExperiencedQualification(
    payload: AcceptExperiencedQualificationRequest,
  ): Observable<UserAccount> {
    return this.http.put<UserAccount>(
      `${this.API_URL}/AcceptExperiencedQualification`,
      payload,
    );
  }

  setInvestorStatement(payload: SetInvestmentStateRequest) {
    return this.http.put<UserAccount>(`${this.API_URL}/SetStatement`, payload);
  }

  setKnowledgeTest(payload: SetKnowledgeTestRequest) {
    return this.http.put<UserAccount>(
      `${this.API_URL}/SetKnowledgeTest`,
      payload,
    );
  }

  acceptKnowledgeTest(payload: AcceptKnowledgeTestRequest) {
    return this.http.put<UserAccount>(
      `${this.API_URL}/SetKnowledgeLossTest`,
      payload,
    );
  }

  getSubAccounts(): Observable<PaginatedData<LegalEntityDetails>> {
    return this.http.get<PaginatedData<LegalEntityDetails>>(
      `${this.API_URL}/SubAccountList`,
    );
  }
}
